<div *ngIf="isOpen" class="fixed inset-0 z-[100] flex justify-end" style="top: 64px">
  <div class="absolute inset-0 bg-black/30" (click)="close()"></div>

  <div class="relative w-full max-w-2xl bg-white shadow-xl flex flex-col h-full border-l border-gray-200">
    <header class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
      <div>
        <h2 class="text-lg font-semibold text-gray-900">Distribution groups</h2>
        <p class="text-sm text-gray-500 mt-0.5">
          Global groups (GM) appear under Assign To for everyone. Private groups are only yours.
        </p>
      </div>
      <button type="button" (click)="close()" class="p-2 rounded-full text-gray-500 hover:bg-gray-100" aria-label="Close">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </header>

    <div class="flex-1 overflow-y-auto p-4 space-y-6">
      <p *ngIf="loadingGroups" class="text-sm text-gray-500">Loading groups…</p>

      <section *ngIf="showForm && editing" class="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-4">
        <h3 class="text-sm font-semibold text-gray-900">
          {{ editing._id ? 'Edit' : 'New' }}
          {{ editing.isGlobal ? 'global' : 'private' }} group
        </h3>
        <div>
          <label class="block text-sm font-medium text-gray-700">Name</label>
          <input
            type="text"
            [(ngModel)]="editing.name"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
            placeholder="e.g. All Estimators"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Description (optional)</label>
          <input
            type="text"
            [(ngModel)]="editing.description"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>

        <div>
          <p class="text-sm font-medium text-gray-700 mb-2">Roles</p>
          <div class="max-h-36 overflow-y-auto space-y-1 border border-gray-200 rounded-md p-2 bg-white">
            <label *ngFor="let role of systemRoles" class="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                [checked]="editing.roleIds.includes(role._id)"
                (change)="toggleRoleId(role._id, $any($event.target).checked)"
              />
              {{ role.name }}
            </label>
            <p *ngIf="systemRoles.length === 0" class="text-sm text-gray-500">No roles available</p>
          </div>
        </div>

        <div>
          <p class="text-sm font-medium text-gray-700 mb-2">Users</p>
          <div class="max-h-36 overflow-y-auto space-y-1 border border-gray-200 rounded-md p-2 bg-white">
            <label *ngFor="let user of tenantUsers" class="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                [checked]="editing.userIds.includes(user._id)"
                (change)="toggleUserId(user._id, $any($event.target).checked)"
              />
              {{ user.name }}
            </label>
          </div>
        </div>

        <div class="flex gap-2 justify-end">
          <button type="button" (click)="cancelForm()" class="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="button"
            (click)="save()"
            [disabled]="saving"
            class="px-3 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {{ saving ? 'Saving…' : 'Save group' }}
          </button>
        </div>
      </section>

      <section>
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-semibold text-gray-900">Global groups</h3>
          <button
            *ngIf="canCreateGlobal"
            type="button"
            (click)="startCreate(true)"
            class="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
          >
            + Add global
          </button>
        </div>
        <p *ngIf="!canCreateGlobal" class="text-xs text-gray-500 mb-2">Created by your general manager for the shop.</p>
        <ul class="divide-y divide-gray-200 rounded-lg border border-gray-200">
          <li *ngFor="let group of globalGroups" class="px-3 py-3 flex items-start justify-between gap-3">
            <div>
              <p class="font-medium text-gray-900">{{ group.name }}</p>
              <p class="text-xs text-gray-500">{{ memberSummary(group) }}</p>
            </div>
            <div *ngIf="canCreateGlobal" class="flex shrink-0 gap-2">
              <button type="button" (click)="startEdit(group)" class="text-sm text-gray-600 hover:text-gray-900">Edit</button>
              <button type="button" (click)="confirmDelete(group)" class="text-sm text-red-600 hover:text-red-800">Delete</button>
            </div>
          </li>
          <li *ngIf="globalGroups.length === 0" class="px-3 py-4 text-sm text-gray-500">No global groups yet.</li>
        </ul>
      </section>

      <section>
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-semibold text-gray-900">Private groups</h3>
          <button type="button" (click)="startCreate(false)" class="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
            + Add private
          </button>
        </div>
        <ul class="divide-y divide-gray-200 rounded-lg border border-gray-200">
          <li *ngFor="let group of privateGroups" class="px-3 py-3 flex items-start justify-between gap-3">
            <div>
              <p class="font-medium text-gray-900">{{ group.name }}</p>
              <p class="text-xs text-gray-500">{{ memberSummary(group) }}</p>
            </div>
            <div class="flex shrink-0 gap-2">
              <button type="button" (click)="startEdit(group)" class="text-sm text-gray-600 hover:text-gray-900">Edit</button>
              <button type="button" (click)="confirmDelete(group)" class="text-sm text-red-600 hover:text-red-800">Delete</button>
            </div>
          </li>
          <li *ngIf="privateGroups.length === 0" class="px-3 py-4 text-sm text-gray-500">No private groups yet.</li>
        </ul>
      </section>
    </div>
  </div>
</div>

<lib-confirmation-modal-tw
  class="z-[9999]"
  [isOpen]="showDeleteModal"
  [title]="'Delete distribution group'"
  [message]="'Remove this group from Assign To? Existing tasks are not changed.'"
  [confirmButtonText]="'Delete'"
  [cancelButtonText]="'Cancel'"
  [type]="'reject'"
  (confirm)="deleteGroup()"
  (cancel)="showDeleteModal = false; deleteTarget = null"
></lib-confirmation-modal-tw>
